import { MealTime } from "@/clients/models/MealTime";
import { useOpenChatAtMessage } from "@/features/ai-chat/hooks/use-open-chat-at-message";
import { isChatCacheExpired } from "@/stores/chat.store";
import { planFoodBadgeMealsAtom, planFoodBadgesAtom } from "@/stores/plan-food-add.store";
import { useAtomValue, useSetAtom } from "jotai";
import { memo, useCallback } from "react";
import { useOpenMealCoach } from "../../hooks/use-open-meal-coach";
import { SparkleButton } from "./sparkle-button";

interface MealCoachButtonProps {
  mealTime: MealTime;
  /** Plan day key, e.g. "tuesday". */
  mealDay: string;
  /** ISO date (yyyy-MM-dd) for the selected plan day, when available. */
  targetDate?: string | null;
  /** Meal name when the slot is filled; omit for an empty slot. */
  mealName?: string | null;
  mealId?: string | null;
  className?: string;
}

/**
 * Sparkle button shown on My Plan v2 meal cards / empty slots. Normally opens
 * the NutriCoach chat seeded with context about this meal time. While the meal
 * carries a "just added" badge, a tap instead reopens the live chat at the
 * "Added to your plan" message that created it and clears the badge.
 */
export const MealCoachButton = memo(function MealCoachButton({
  mealTime,
  mealDay,
  targetDate,
  mealName,
  mealId,
  className,
}: MealCoachButtonProps) {
  const openMealCoach = useOpenMealCoach();
  const openChatAtMessage = useOpenChatAtMessage();
  const badgeMeals = useAtomValue(planFoodBadgeMealsAtom);
  const setBadges = useSetAtom(planFoodBadgesAtom);
  const badgeMessageId = mealId ? badgeMeals.get(mealId) : undefined;

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      // The card itself opens the meal detail on click — don't trigger both.
      event.stopPropagation();
      if (badgeMessageId && mealId) {
        if (isChatCacheExpired()) {
          // The transcript expired while the session id stayed in memory:
          // the message is gone, so drop every badge and start a normal chat.
          setBadges([]);
        } else {
          setBadges((prev) => prev.filter((badge) => badge.mealId !== mealId));
          openChatAtMessage(badgeMessageId);
          return;
        }
      }
      openMealCoach({ mealTime, day: mealDay, targetDate, mealName, mealId });
    },
    [
      badgeMessageId,
      mealId,
      setBadges,
      openChatAtMessage,
      openMealCoach,
      mealTime,
      mealDay,
      targetDate,
      mealName,
    ],
  );

  return (
    <SparkleButton
      aria-label={
        badgeMessageId ? "See how NutriCoach added this meal" : "Ask NutriCoach about this meal"
      }
      badge={Boolean(badgeMessageId)}
      onClick={handleClick}
      className={className}
    />
  );
});
